import React from 'react';

import DateFnsUtils from '@date-io/date-fns';
import PropTypes from 'prop-types';
import plLocale from 'date-fns/locale/pl';
import enLocale from 'date-fns/locale/en-US';
import { DateTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { IconButton, InputAdornment, Grid } from '@material-ui/core';
import { MuiThemeProvider } from '@material-ui/core/styles';
import moment from 'moment-timezone';

import UserSessionDataHandler from 'auth/UserSessionsDataHandler';
import Icons from 'consts/icons';
import { userTheme } from 'consts/config';

import { useSelector } from 'react-redux';
import { FiltersDetailsUseStyles } from 'components/common/components/filters/FiltersUseStyles';
import { darkTheme, lightTheme, useStyles } from './TimePickerUseStyles';

const LocaleMap = {
  pl: plLocale,
  en: enLocale,
};

const TimePicker = props => {
  const {
    restrictPastDates,
    selectedEndDate,
    selectedStartDate,
    handleEndDateChange,
    handleStartDateChange,
    setError,
    classesProps,
  } = props;
  const language = useSelector(state => state.language);
  const timezone = useSelector(state => state.timezone);
  const userSettingObject = UserSessionDataHandler.getUserSettingsObject();
  const lang = userSettingObject[UserSessionDataHandler.defaultLanguage];

  let error1 = false;
  let error2 = false;
  const now = new Date().getTime();
  const oneMinuteInMiliseconds = 60000;
  const momentOfNow = moment(now);
  const momentOfUTC = moment(now).tz('UTC');
  const offset =
    (momentOfNow.utcOffset() - momentOfUTC.utcOffset()) *
    oneMinuteInMiliseconds;

  const classes = useStyles();
  const classesFilter = FiltersDetailsUseStyles();

  const handleFirstError = (error, value) => {
    if (error && value) {
      error1 = true;
    } else if (!error && value) {
      error1 = false;
    }
    if (error1 || error2) setError(true);
    else setError(false);
  };
  const handleSecondError = (error, value) => {
    if (error && value) {
      error2 = true;
    } else if (!error && value) {
      error2 = false;
    }
    if (error1 || error2) setError(true);
    else setError(false);
  };

  const returnInitialDate = (inMiliseconds = false) => {
    const modifiedDate = moment(now);
    modifiedDate
      .subtract(momentOfNow.utcOffset() - momentOfUTC.utcOffset(), 'minutes')
      .valueOf();
    if (inMiliseconds) return modifiedDate.valueOf();
    return modifiedDate;
  };

  const handleOnChangeStartTime = value => {
    handleStartDateChange(
      new Date(new Date(value).getTime() + offset).getTime()
    );
  };

  const handleOnChangeEndTime = value => {
    handleEndDateChange(new Date(new Date(value).getTime() + offset).getTime());
  };

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils} locale={LocaleMap[lang]}>
      <MuiThemeProvider
        theme={
          UserSessionDataHandler.getTheme() === userTheme.dark
            ? darkTheme
            : lightTheme
        }
      >
        <Grid container direction="row" spacing={1}>
          <Grid item xs={6}>
            <DateTimePicker
              autoOk
              ampm={false}
              initialFocusedDate={returnInitialDate()}
              minDate={restrictPastDates ? returnInitialDate(true) : false}
              variant="dialog"
              format="yyyy/MM/dd HH:mm"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton>
                      <Icons.Misc.Calendar
                        className={`${classes.TimePicker}__label__icon ${classes.TimePicker}__label--color`}
                      />
                    </IconButton>
                  </InputAdornment>
                ),
                classes: {
                  root: `${classesFilter.FiltersDetails}__table__filters__add__form__label`,
                  underline: `${classesFilter.FiltersDetails}__table__filters__add__form__underline`,
                  input: `${classes.FiltersDetails}__table__filters__add__form--color`,
                },
              }}
              InputLabelProps={{
                classes: {
                  formControl: `${classesFilter.FiltersDetails}__table__filters__add__form__label`,
                  focused: `${classesFilter.FiltersDetails}__table__filters__add__form__label--focused`,
                  error: `${classes.TimePicker}__label__info`,
                },
              }}
              FormHelperTextProps={{
                classes: {
                  error: `${classes.TimePicker}__label__info ${classes.TimePicker}__label__info--help`,
                },
              }}
              margin="none"
              id="date-picker-dialog"
              allowKeyboardControl={false}
              label={`${language.missionsDetails.calendarFromDate_} [${timezone}]`}
              value={
                selectedStartDate
                  ? new Date(new Date(selectedStartDate).getTime() - offset)
                  : null
              }
              onChange={handleOnChangeStartTime}
              cancelLabel={language.missionsDetails.calendarCancel_}
              okLabel={language.missionsDetails.calendarOk_}
              strictCompareDates
              onError={restrictPastDates ? handleFirstError : undefined}
              className={classesProps}
            />
          </Grid>
          <Grid item xs={6}>
            <DateTimePicker
              autoOk
              ampm={false}
              disabled={selectedStartDate === null}
              margin="none"
              initialFocusedDate={
                selectedStartDate
                  ? new Date(new Date(selectedStartDate).getTime() - offset)
                  : returnInitialDate()
              }
              minDate={
                selectedStartDate
                  ? new Date(new Date(selectedStartDate).getTime() - offset)
                  : null
              }
              format="yyyy/MM/dd HH:mm"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton>
                      <Icons.Misc.Calendar
                        className={`${classes.TimePicker}__label__icon ${classes.TimePicker}__label--color`}
                      />
                    </IconButton>
                  </InputAdornment>
                ),
                classes: {
                  root: `${classesFilter.FiltersDetails}__table__filters__add__form__label`,
                  underline: `${classesFilter.FiltersDetails}__table__filters__add__form__underline`,
                  input: `${classes.FiltersDetails}__table__filters__add__form--color`,
                },
              }}
              InputLabelProps={{
                classes: {
                  formControl: `${classesFilter.FiltersDetails}__table__filters__add__form__label`,
                  focused: `${classesFilter.FiltersDetails}__table__filters__add__form__label--focused`,
                  error: `${classes.TimePicker}__label__info`,
                },
              }}
              FormHelperTextProps={{
                classes: {
                  error: `${classes.TimePicker}__label__info ${classes.TimePicker}__label__info--help`,
                },
              }}
              id="date-picker-dialog2"
              label={`${language.missionsDetails.calendarToDate_} [${timezone}]`}
              value={
                selectedEndDate
                  ? new Date(new Date(selectedEndDate).getTime() - offset)
                  : null
              }
              onChange={handleOnChangeEndTime}
              variant="dialog"
              cancelLabel={language.missionsDetails.calendarCancel_}
              okLabel={language.missionsDetails.calendarOk_}
              strictCompareDates
              onError={restrictPastDates ? handleSecondError : undefined}
              className={classesProps}
            />
          </Grid>
        </Grid>
      </MuiThemeProvider>
    </MuiPickersUtilsProvider>
  );
};

TimePicker.propTypes = {
  restrictPastDates: PropTypes.bool,
  selectedStartDate: PropTypes.oneOfType([
    PropTypes.object.isRequired,
    PropTypes.oneOf([PropTypes.instanceOf(null)]).isRequired,
  ]),
  selectedEndDate: PropTypes.oneOfType([
    PropTypes.object.isRequired,
    PropTypes.oneOf([PropTypes.instanceOf(null)]).isRequired,
  ]),
  handleStartDateChange: PropTypes.func.isRequired,
  handleEndDateChange: PropTypes.func.isRequired,
  setError: PropTypes.func,
  classesProps: PropTypes.string,
};

export default TimePicker;
